import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { client } from '../../api/client';
import cookie from '../../../storage/cookie';
import layout from '../../../ui/layout';
import analytics from '../../../utils/analytics';
import logger from '../../../utils/logger';
import recaptcha from '../../../utils/recaptcha';

export const Status = Object.freeze({
	Uninitialised: 'uninitialised',
	Loading: 'loading',
	Succeeded: 'succeeded',
	Failed: 'failed'
});

const initialState = {
	error: null,
	filterState: {
		encodedQueryState: null,
		resultsUrl: null,
		searchType: null,
		totalResultCount: -1,
		navigatedModelId: null,
		engineItems: [],
		navigatedEngine: null,
		trimItems: [],
		navigatedTrim: null,
		electricRangeItems: [],
		navigatedElectricRange: null,
		energyConsumptionItems: [],
		navigatedEnergyConsumption: null,
		fuelTypeItems: [],
		navigatedFuelType: null,
		insuranceGroupItems: [],
		navigatedInsuranceGroup: null,
		milesPerGallonItems: [],
		navigatedMilesPerGallon: null,
		milesPerTankItems: [],
		navigatedMilesPerTank: null,
		numberOfDoorsItems: [],
		navigatedNumberOfDoors: null,
		powerItems: [],
		navigatedPower: null,
		transmissionItems: [],
		navigatedTransmission: null,
		yearItems: [],
		navigatedYear: null,
		zeroToSixtyItems: [],
		navigatedZeroToSixty: null,
		sortOptions: []
	},
	filterStatus: Status.Uninitialised,
	openFilterName: null,
	openFilterShouldForceRefresh: false,
	results: null,
	resultsStatus: Status.Uninitialised,
	updateResults: false,
	vrm: {
		confirmPageUrl: null,
		errorMessage: null,
		recaptchaKey: null,
	}
};

const makeResetApiData = (state) => {
	const filterState = state.filterState;

	return {
		modelId: filterState.navigatedModelId?.value
	};
}

const makeApiData = (state, filterName, filterValue) => {
	const filterState = state.filterState;

	const result = {
		modelId: filterState.navigatedModelId?.value,
		engine: filterState.navigatedEngine?.value,
		trim: filterState.navigatedTrim?.value,
		electricRange: filterState.navigatedElectricRange?.value,
		energyConsumption: filterState.navigatedEnergyConsumption?.value,
		fuelType: filterState.navigatedFuelType?.value,
		insuranceGroup: filterState.navigatedInsuranceGroup?.value,
		milesPerGallon: filterState.navigatedMilesPerGallon?.value,
		milesPerTank: filterState.navigatedMilesPerTank?.value,
		numberOfDoors: filterState.navigatedNumberOfDoors?.value,
		power: filterState.navigatedPower?.value,
		transmission: filterState.navigatedTransmission?.value,
		year: filterState.navigatedYear?.value,
		zeroToSixty: filterState.navigatedZeroToSixty?.value
	};

	result[filterName] = filterValue;

	return result;
};

export const fetchUpdatedFilterState = createAsyncThunk(
	'specsSearch/fetchUpdatedFilterState',
	async (filter, { getState }) => {
		const { specsSearch } = getState();

		const apiData = makeApiData(specsSearch, filter.name, filter.value);

		const response = await client.post(
			'/api/specs-search/filters/',
			apiData
		);

		return {
			filterState: response.data,
			updateResults: filter.forceResultRefresh || layout.isDesktop()
		};
	}
);

export const fetchInitialState = createAsyncThunk(
	'specsSearch/fetchInitialState',
	async (data) => {
		const { initialState, recaptchaKey } = data;
		const response = await client.get(
			'/api/specs-search/initial-filters/' + initialState
		);

		return {
			filterState: response.data,
			recaptchaKey: recaptchaKey
		};
	},
	{
		condition: (filter, { getState }) => {
			const { specsSearch } = getState();

			if (specsSearch.filterStatus !== Status.Uninitialised) {
				return false;
			}
		}
	}
);

export const fetchResetFilterState = createAsyncThunk(
	'specsSearch/fetchResetFilterState',
	async (data, { getState }) => {
		const { specsSearch } = getState();

		const apiData = makeResetApiData(specsSearch);

		const response = await client.post(
			'/api/specs-search/filters/',
			apiData
		);

		return response.data;
	}
);

export const fetchResults = createAsyncThunk(
	'specsSearch/fetchResults',
	async (encodedQueryState) => {
		const response = await client.getHtml(
			'/_component/car-specs/search-results/?_qs=' + encodedQueryState
		);

		return response.data;
	}
);

export const submitVrm = createAsyncThunk(
	'specsSearch/submitVrm',
	async (vrm, { getState }) => {
		const { specsSearch } = getState();

		const recaptchaSiteKey = specsSearch.vrm.recaptchaKey;
		let recaptchaToken;

		try {
			recaptchaToken = await recaptcha.v3ExecuteAsync(recaptchaSiteKey, 'submit');
		} catch (error) {
			logger.error(error);
		}

		let errorMessage = null;
		let confirmationPageUrl = null;

		if (recaptchaToken) {
			const response = await client.post(
				'/api/cars/vrm/',
				{
					vrm: vrm,
					pathSegment: 'car-specs',
					recaptchaResponse: recaptchaToken,
					isVersion3: true,
					storeIfSuccessful: true
				}
			);

			errorMessage = response.data.errorMessage;

			if (response.data.isSuccess) {
				analytics.trackEvent('VRM Lookup', 'Successful VRM Lookup', 'Specs search');
				cookie.setCookie(response.data.vrmCookieName, response.data.vrm);
				confirmationPageUrl = response.data.confirmPageUrl;
			}
		} else {
			errorMessage = 'An error occurred, please try again or search by make and model';
		}

		return {
			confirmationPageUrl: confirmationPageUrl,
			errorMessage: errorMessage
		};
	}
);

export const specsSearchSlice = createSlice({
	name: 'specsSearch',
	initialState,
	reducers: {
		filtersClosed(state) {
			state.openFilterName = '';

			if (!layout.isDesktop()) {
				state.updateResults = true;
			}
		},
		setOpenFilterName(state, action) {
			if (action.payload) {
				const { filterName, forceResultRefresh } = action.payload;
				state.openFilterName = filterName;
				state.openFilterShouldForceRefresh = forceResultRefresh;
			} else {
				state.openFilterName = null;
				state.openFilterShouldForceRefresh = false;
			}
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUpdatedFilterState.pending, (state) => {
				state.filterStatus = Status.Loading;
				state.openFilterName = null;
			})
			.addCase(fetchUpdatedFilterState.fulfilled, (state, action) => {
				const { filterState, updateResults } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.filterState = filterState;
				state.updateResults = updateResults;
			})
			.addCase(fetchUpdatedFilterState.rejected, (state, action) => {
				state.filterStatus = Status.Failed;

				state.error = action.error.message;
			});

		builder
			.addCase(fetchInitialState.pending, (state) => {
				state.filterStatus = Status.Loading;
			})
			.addCase(fetchInitialState.fulfilled, (state, action) => {
				const { filterState, recaptchaKey } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.filterState = filterState;
				state.vrm.recaptchaKey = recaptchaKey;
			})
			.addCase(fetchInitialState.rejected, (state, action) => {
				state.filterStatus = Status.Failed;
				state.error = action.error.message;
			});

		builder
			.addCase(fetchResetFilterState.pending, (state) => {
				state.filterStatus = Status.Loading;
				state.openFilterName = null;
			})
			.addCase(fetchResetFilterState.fulfilled, (state, action) => {
				state.filterStatus = Status.Succeeded;
				state.filterState = action.payload;
				state.updateResults = layout.isDesktop();
			})
			.addCase(fetchResetFilterState.rejected, (state) => {
				state.filterStatus = Status.Failed;
			});

		builder
			.addCase(fetchResults.pending, (state) => {
				state.resultsStatus = Status.Loading;
			})
			.addCase(fetchResults.fulfilled, (state, action) => {
				state.resultsStatus = Status.Succeeded;
				state.results = action.payload;
				state.updateResults = false;
			})
			.addCase(fetchResults.rejected, (state, action) => {
				state.resultsStatus = Status.Failed;
				state.error = action.error.message;
				state.updateResults = false;
			});

		builder
			.addCase(submitVrm.pending, (state) => {
				state.filterStatus = Status.Loading;
				state.vrm.errorMessage = null;
			})
			.addCase(submitVrm.fulfilled, (state, action) => {
				const { confirmationPageUrl, errorMessage } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.vrm.confirmationPageUrl = confirmationPageUrl;
				state.vrm.errorMessage = errorMessage;
			})
			.addCase(submitVrm.rejected, (state) => {
				state.filterStatus = Status.Failed;
				state.vrm.errorMessage = 'An error occurred, please try again';
			});
	}
});

export const { filtersClosed, setOpenFilterName, setRecaptchaKey } = specsSearchSlice.actions;

export default specsSearchSlice.reducer;